import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ExecutionBusinessLine, UpdateRevenueGoalsRequest } from '@monitoring-panel/types';
import { api } from '@/services/api';

export function useGlobalRevenue(
  startDate: string,
  endDate: string,
  scope: ExecutionBusinessLine,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['global-revenue', scope, startDate, endDate],
    queryFn: () => api.revenue.getGlobal(startDate, endDate, scope),
    enabled,
  });
}

export function useGeoRevenue(startDate: string, endDate: string, enabled: boolean) {
  return useQuery({
    queryKey: ['geo-revenue', startDate, endDate],
    queryFn: () => api.revenue.getGeo(startDate, endDate),
    enabled,
  });
}

export function useConsolidatedRevenue(startDate: string, endDate: string, enabled: boolean) {
  return useQuery({
    queryKey: ['consolidated-revenue', startDate, endDate],
    queryFn: () => api.revenue.getConsolidated(startDate, endDate),
    enabled,
  });
}

export function useRevenueGoals() {
  return useQuery({
    queryKey: ['revenue-goals'],
    queryFn: () => api.revenue.getGoals(),
  });
}

/** Save goals; every revenue view depends on them, so all are refetched. */
export function useUpdateRevenueGoals() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateRevenueGoalsRequest) => api.revenue.updateGoals(data),
    onSuccess: () => {
      for (const key of ['revenue-goals', 'global-revenue', 'geo-revenue', 'consolidated-revenue']) {
        queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}
